"use client";

import Image from "next/image";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import {
  LuClipboardList,
  LuGraduationCap,
  LuMessageCircle,
  LuNewspaper,
  LuSearch,
  LuSmartphone,
  LuTarget,
} from "react-icons/lu";
import { useAuthStore } from "@/store/useAuthStore";

// ─── Constants ─────────────────────────────────────────────────────────────

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.xephyrlabs.lattuai";

const features = [
  {
    icon: LuSearch,
    title:
      "Find programs, universities and countries that fit your research area.",
    body: "Compare countries and see which one fits your situation. See which universities are actively looking for students in agricultural science, bioinformatics, food technology, fisheries, and life sciences and match your academic profile specifically.",
  },
  {
    icon: LuTarget,
    title: "Get a personalised study abroad plan.",
    body: "A step-by-step plan from university application to settling abroad, built around your specific target programs and countries.",
  },
  {
    icon: LuGraduationCap,
    title: "Discover scholarships that you can apply for.",
    body: "Find scholarships that match your field, your program, and university. When you create your plan with Lattu AI, you will get a personalized list of scholarships. Aside from that, you can also explore our scholarship hub.",
  },
  {
    icon: LuClipboardList,
    title: "Track everything in one place.",
    body: "Track every step of your study abroad process: application deadlines, required documents, visa steps — all in one place.",
  },
  {
    icon: LuMessageCircle,
    title: "Join a community of students on the same path.",
    body: "Connect with Bangladeshi students who are already applying, already accepted, or already abroad. Ask questions and share your experience.",
  },
  {
    icon: LuNewspaper,
    title: "Stay current with news and updates that matter to your field.",
    body: "Policy changes, visa updates, and regulation shifts that affect your plans, curated so you don't miss what matters.",
  },
];

// ─── Page ──────────────────────────────────────────────────────────────────

export default function EventContent() {
  const posthog = usePostHog();
  const { user } = useAuthStore();
  const journeyHref = user
    ? "/journey-planner"
    : "/login/?redirect=/journey-planner";

  const trackCta = (placement: string, ctaText: string) =>
    posthog?.capture("hero_cta_clicked", {
      placement,
      source_page: "study_abroad_agriculture_bioinformatics_bangladesh",
      cta_text: ctaText,
      is_logged_in: !!user,
    });

  const trackAppDownload = (placement: string) =>
    posthog?.capture("app_download_click", {
      app_platform: "android",
      placement,
      source_page: "study_abroad_agriculture_bioinformatics_bangladesh",
      cta_text: "get_android_app",
    });

  return (
    <div className="w-full bg-transparent">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-20 pb-16 lg:pt-28 lg:pb-20 bg-transparent">
        <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-200 h-100 bg-indigo-500/10 rounded-full blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left — copy */}
            <div className="lg:col-span-7">
              <h1 className="text-3xl lg:text-5xl font-black tracking-tight leading-tight mb-5 text-black dark:text-white">
                Plan higher studies abroad in agriculture, bioinformatics and
                related life sciences
              </h1>

              <p className="text-lg lg:text-xl leading-relaxed mb-8 text-zinc-600 dark:text-zinc-400 max-w-2xl">
                Explore the study paths, destinations, funding routes and
                decisions that matter for your field. Then create a personal
                plan based on your academic background, budget, goals and
                preferences with Lattu AI.
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-4">
                <Link
                  href={journeyHref}
                  onClick={() =>
                    trackCta("hero", "create_your_study_abroad_plan")
                  }
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-sm bg-[#615fff] text-white hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-[#615fff]/20"
                >
                  Create your study-abroad plan
                  <span aria-hidden>→</span>
                </Link>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackAppDownload("hero")}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-sm border border-gray-300 dark:border-white/20 text-black dark:text-white hover:border-[#615fff]/50 active:scale-95 transition-all"
                >
                  <LuSmartphone size={16} />
                  Download the App
                </a>
              </div>

              <p className="text-xs text-zinc-500 dark:text-zinc-500 mb-8">
                Free to start. It takes around 5 minutes to tell us about your
                goals and create a plan.
              </p>

              {/* Partnership card */}
              <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-white/5 w-fit">
                <span className="text-xs text-zinc-500 whitespace-nowrap">
                  In partnership with
                </span>
                <div className="bg-white rounded-md p-1 shrink-0">
                  {/* eslint-disable-next-line @next/next/no-img-element -- small static crest, no need for next/image optimization pipeline */}
                  <img
                    src="/images/BAU_Logo.png"
                    alt="Bangladesh Agricultural University"
                    className="h-8 w-auto object-contain"
                  />
                </div>
                <div className="w-px h-8 bg-zinc-200 dark:bg-white/10 hidden sm:block" />
                <span className="text-xs text-zinc-500 dark:text-zinc-400">
                  For students participating in the Build with AI Hack Days @BAU
                  event
                </span>
              </div>
            </div>

            {/* Right — hero image */}
            <div className="lg:col-span-5 relative flex justify-center">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full bg-indigo-500/20 blur-[80px] pointer-events-none" />
              <Image
                src="/images/Event_heroImg.png"
                alt="study abroad in agriculture, bioinformatics and related life sciences"
                width={600}
                height={554}
                priority
                className="relative z-10 w-full h-auto max-w-md object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 1: Your field has global pull ───────────────────────── */}
      <section className="py-20 lg:py-24 bg-[#ECEFF1] dark:bg-[#1a1a1a] transition-colors">
        <div className="max-w-5xl mx-auto px-6">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500 block mb-3">
            Why It Matters
          </span>
          <h2 className="text-3xl lg:text-4xl font-black tracking-tight text-black dark:text-white mb-6">
            Your field has global pull. Most students don&apos;t know it yet.
          </h2>
          <div className="space-y-4">
            <p className="text-base lg:text-lg leading-relaxed text-zinc-600 dark:text-zinc-400">
              Food security, climate-resilient agriculture, drug discovery,
              genomics, aquatic ecosystems - these aren&apos;t local problems.
              They&apos;re the defining research priorities of the next decade.
            </p>
            <p className="text-base lg:text-lg leading-relaxed text-zinc-600 dark:text-zinc-400">
              Universities in the Netherlands, Germany, Japan, Canada, and
              Australia are building entire research programs around exactly
              these topics. They need graduate students who bring real data,
              real fieldwork, and real context from the places where these
              challenges are most urgent.
            </p>
            <p className="text-base lg:text-lg leading-relaxed text-zinc-600 dark:text-zinc-400">
              That&apos;s not a generic description. That&apos;s a Bangladeshi
              student studying agricultural science, fisheries, biotechnology,
              or bioinformatics.{" "}
              <strong className="text-black dark:text-white font-semibold">
                That&apos;s you.
              </strong>
            </p>
            <p className="text-base lg:text-lg leading-relaxed text-zinc-600 dark:text-zinc-400">
              The gap isn&apos;t your qualification. It&apos;s knowing where to
              go, how to apply, and what to say.
            </p>
          </div>
        </div>
      </section>

      {/* ── Section 2: Everything you need ───────────────────────────────── */}
      <section className="py-20 lg:py-24 bg-transparent">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12 space-y-3">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500 block">
              The Platform
            </span>
            <h2 className="text-3xl lg:text-4xl font-black tracking-tight text-black dark:text-white">
              Everything you need to plan, apply, and connect
            </h2>
            <p className="text-base lg:text-lg max-w-2xl mx-auto leading-relaxed text-zinc-600 dark:text-zinc-400">
              Lattu AI is a study abroad planning platform built for Bangladeshi
              students. You tell us your background, your goals, and your
              budget, and it maps out a realistic plan to your next degree
              abroad.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {features.map((item) => (
              <div
                key={item.title}
                className="rounded-2xl p-6 space-y-4 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#615fff]/10">
                  <item.icon size={20} className="text-[#615fff]" />
                </div>
                <h3 className="font-bold text-base text-black dark:text-white">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Section 3: Get started today ─────────────────────────────────── */}
      <section className="py-24 bg-[#615fff]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-black mb-6 text-white">
            Get started today
          </h2>
          <p className="text-lg mb-10 max-w-2xl mx-auto font-medium text-white/90">
            It takes five minutes to set up your profile and see which graduate
            programs abroad actually match your research background and goals.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            <Link
              href={journeyHref}
              onClick={() => trackCta("final_cta", "start_planning_for_free")}
              className="inline-flex items-center gap-2 px-10 py-4 rounded-xl font-black text-lg hover:brightness-110 active:scale-95 transition-all shadow-xl bg-white text-[#615fff] shadow-black/10"
            >
              Start Planning for Free
            </Link>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackAppDownload("final_cta")}
              className="inline-flex items-center gap-2 px-10 py-4 rounded-xl font-black text-lg border-2 border-white text-white hover:bg-white/10 active:scale-95 transition-all"
            >
              Download the Lattu AI App
            </a>
          </div>
          <p className="text-sm text-white/80">
            Available on web and mobile. Free to use.
          </p>
        </div>
      </section>
    </div>
  );
}
